/**
 * Front matter is one flat record, one `key: value` line per field. A string is a plain scalar (`title: Doc`, README
 * "Front Matter") unless YAML needs quotes; then, and for every non-string, the value is JSON, a YAML subset.
 * Reading splits each line at the first `: `, JSON-parses the value and falls back to the raw trimmed text.
 */

export function dumpFlatYaml(record: Record<string, unknown>): string {
  return Object.entries(record)
    .filter(([, value]) => value !== undefined)
    .map(([key, value]) => `${key}: ${typeof value === 'string' && isPlainSafe(value) ? value : JSON.stringify(value)}`)
    .join('\n')
}

// When a string needs quotes, after js-yaml's dump (isPlainSafeFirst, isPlainSafe and the implicit-type resolvers in
// js-yaml/lib/dumper.js and lib/type/*): a leading indicator character, `: ` or ` #` inside, a trailing `:`, leading or
// trailing whitespace, a control character or newline, or text that would load as a bool, null, number or timestamp.
const leadingIndicator = /^[-?:,[\]{}#&*!|>'"%@`]/
const otherType = /^(?:~|null|true|false|yes|no|on|off|y|n|[-+]?(?:\d[\d_]*(?:\.[\d_]*)?|\.\d[\d_]*)(?:e[-+]?\d+)?|0x[\da-f_]+|0o?[0-7_]+|0b[01_]+|[-+]?\.(?:inf|nan)|\d{4}-\d\d?-\d\d?(?:[Tt ].*)?)$/i
function isPlainSafe(value: string): boolean {
  return value !== '' && value.trim() === value && !hasControlCharacter(value) && !leadingIndicator.test(value)
    && !value.includes(': ') && !value.includes(' #') && !value.endsWith(':') && !otherType.test(value)
}

/** Returns null for unparseable front matter: a non-empty line without `: `, or an unclosed flow or quoted value. */
export function loadFlatYaml(text: string): Record<string, unknown> | null {
  const record: Record<string, unknown> = {}
  for (const line of text.split('\n')) {
    if (!line.trim()) continue
    const separator = line.indexOf(': ')
    if (separator === -1) return null
    const key = line.slice(0, separator).trim()
    const raw = line.slice(separator + 2).trim()
    try {
      record[key] = JSON.parse(raw)
    } catch {
      // Not JSON: a plain scalar. One that opens a flow collection or a quoted scalar is malformed YAML.
      if (/^[[{"']/.test(raw)) return null
      record[key] = raw
    }
  }
  return record
}

function hasControlCharacter(value: string): boolean {
  for (let index = 0; index < value.length; index++) {
    const code = value.charCodeAt(index)
    if (code < 32 || code === 127) return true
  }
  return false
}
